import React, { useEffect, useRef } from 'react';
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, Minimize2 } from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import { CALL_STATE } from './useCall';
import { getInitials, getSenderColor } from './useChat';

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.5); }
  50% { box-shadow: 0 0 0 20px rgba(255,255,255,0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: linear-gradient(135deg, #1a0033 0%, #4a0e4e 50%, #6b1e6b 100%);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  color: white;
  animation: ${fadeIn} 0.2s ease;
`;

const TopBar = styled.div`
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  -webkit-app-region: drag;
`;

const CallStatus = styled.div`
  font-size: 13px;
  opacity: 0.8;
`;

const Duration = styled.div`
  font-size: 15px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  position: relative;
`;

const AvatarLarge = styled.div`
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: ${p => p.$color ? `linear-gradient(135deg, ${p.$color}, ${p.$color}dd)` : 'rgba(255,255,255,0.2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 48px;
  font-weight: 600;
  margin-bottom: 24px;
  overflow: hidden;
  animation: ${pulse} 2s ease infinite;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const UserName = styled.h1`
  margin: 0 0 8px;
  font-size: 28px;
  font-weight: 600;
`;

const UserSubtext = styled.p`
  margin: 0;
  opacity: 0.7;
  font-size: 14px;
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RemoteVideo = styled.video`
  width: 100%;
  max-height: 70vh;
  border-radius: 16px;
  object-fit: cover;
  background: #222;
`;

const LocalVideo = styled.video`
  position: absolute;
  bottom: 16px;
  right: 16px;
  width: 180px;
  height: 120px;
  border-radius: 12px;
  object-fit: cover;
  background: #333;
  border: 2px solid rgba(255,255,255,0.3);
  box-shadow: 0 4px 16px rgba(0,0,0,0.4);
`;

const Controls = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  padding: 32px 24px 40px;
`;

const CtrlButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$danger ? '#ff3b30' : p.$active ? 'white' : 'rgba(255,255,255,0.15)'};
  color: ${p => p.$danger ? 'white' : p.$active ? '#1a0033' : 'white'};
  transition: all 0.15s ease;

  &:hover {
    transform: scale(1.08);
    background: ${p => p.$danger ? '#dd2d22' : p.$active ? '#f0f0f0' : 'rgba(255,255,255,0.25)'};
  }

  &:active { transform: scale(0.95); }
`;

const ErrorBanner = styled.div`
  position: absolute;
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255,59,48,0.9);
  color: white;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 14px;
`;

const RemoteStateIndicator = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  display: flex;
  gap: 8px;
  background: rgba(0,0,0,0.5);
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
`;

function fmtDuration(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}:${String(m % 60).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function CallScreen({ call }) {
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const localVideoRef = useRef(null);

  // Attach remote stream to audio element (always, so audio plays even in video mode)
  useEffect(() => {
    if (remoteAudioRef.current && call.remoteStream) {
      remoteAudioRef.current.srcObject = call.remoteStream;
      remoteAudioRef.current.play().catch(err => console.warn('[call] audio play failed:', err));
    }
  }, [call.remoteStream]);

  useEffect(() => {
    if (remoteVideoRef.current && call.remoteStream) {
      remoteVideoRef.current.srcObject = call.remoteStream;
      remoteVideoRef.current.play().catch(err => console.warn('[call] video play failed:', err));
    }
  }, [call.remoteStream, call.state]);

  useEffect(() => {
    if (localVideoRef.current && call.localStream) {
      localVideoRef.current.srcObject = call.localStream;
    }
  }, [call.localStream]);

  if (!call.call) return null;

  const { peer, isVideo, isCaller } = call.call;
  const showVideo = isVideo && call.state === CALL_STATE.ACTIVE && call.remoteStream;

  let statusText = '';
  if (call.state === CALL_STATE.CALLING) statusText = 'Вызов...';
  else if (call.state === CALL_STATE.INCOMING) statusText = isVideo ? 'Входящий видеозвонок' : 'Входящий звонок';
  else if (call.state === CALL_STATE.CONNECTING) statusText = 'Соединение...';
  else if (call.state === CALL_STATE.ACTIVE) statusText = '🔒 E2E зашифровано';

  return (
    <Overlay>
      {/* Audio element — plays remote audio when video isn't shown (audio calls) */}
      {!showVideo && (
        <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: 'none' }} />
      )}

      <TopBar>
        <div>
          <CallStatus>{statusText}</CallStatus>
          {call.state === CALL_STATE.ACTIVE && <Duration>{fmtDuration(call.duration)}</Duration>}
        </div>
        <div />
      </TopBar>

      {call.callError && <ErrorBanner>{call.callError}</ErrorBanner>}

      <Body>
        {showVideo ? (
          <VideoContainer>
            <RemoteVideo ref={remoteVideoRef} autoPlay playsInline />
            {call.localStream && !call.videoOff && (
              <LocalVideo ref={localVideoRef} autoPlay playsInline muted />
            )}
            {(call.remoteState.muted || call.remoteState.videoOff) && (
              <RemoteStateIndicator>
                {call.remoteState.muted && <><MicOff size={14} /> Микрофон выкл</>}
                {call.remoteState.videoOff && <><VideoOff size={14} /> Камера выкл</>}
              </RemoteStateIndicator>
            )}
          </VideoContainer>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <AvatarLarge $color={getSenderColor(peer.id)}>
              {peer.avatar
                ? <img src={peer.avatar} alt="" />
                : getInitials(peer.displayName)}
            </AvatarLarge>
            <UserName>{peer.displayName}</UserName>
            <UserSubtext>@{peer.username}</UserSubtext>
          </div>
        )}
      </Body>

      <Controls>
        {call.state === CALL_STATE.INCOMING ? (
          <>
            <CtrlButton $danger onClick={call.rejectCall} title="Отклонить">
              <PhoneOff size={28} />
            </CtrlButton>
            <CtrlButton style={{ background: '#34C759' }} onClick={call.acceptCall} title="Принять">
              <Phone size={28} />
            </CtrlButton>
          </>
        ) : (
          <>
            <CtrlButton $active={call.muted} onClick={call.toggleMute} title={call.muted ? 'Включить микрофон' : 'Выключить микрофон'}>
              {call.muted ? <MicOff size={24} /> : <Mic size={24} />}
            </CtrlButton>
            {isVideo && (
              <CtrlButton $active={call.videoOff} onClick={call.toggleVideo} title={call.videoOff ? 'Включить камеру' : 'Выключить камеру'}>
                {call.videoOff ? <VideoOff size={24} /> : <Video size={24} />}
              </CtrlButton>
            )}
            <CtrlButton $danger onClick={() => call.endCall(false)} title="Завершить">
              <PhoneOff size={28} />
            </CtrlButton>
          </>
        )}
      </Controls>
    </Overlay>
  );
}
